import React from "react";
import { toast } from "sonner";
import {
  PackageX, Calendar, RefreshCw, Loader2, AlertTriangle, RotateCcw,
  ArrowLeftRight, ExternalLink, MapPin, Image as ImageIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { businessApi } from "../../lib/api";

const STATUS_BADGES = {
  failed_delivery: { label: "Mislykket levering", cls: "bg-red-100 text-red-800" },
  redelivery_requested: { label: "Genlevering anmodet", cls: "bg-amber-100 text-amber-800" },
  manual_review: { label: "Under behandling", cls: "bg-blue-100 text-blue-800" },
  returned: { label: "Returneret", cls: "bg-gray-100 text-gray-800" },
};

const REASON_LABELS = {
  recipient_not_home: "Modtager ikke hjemme",
  no_access: "Ingen adgang",
  wrong_address: "Forkert adresse",
  wrong_phone: "Forkert telefon",
  recipient_refused: "Modtager afviste",
  unsafe_location: "Usikker lokation",
  damaged_parcel: "Skadet pakke",
  missing_parcel: "Manglende pakke",
  driver_could_not_locate_address: "Adresse ikke fundet",
  cancelled_by_recipient: "Annulleret af modtager",
  weather_or_traffic: "Vejr/trafik",
  other: "Andet",
};

export default function BusinessFailedReturnsPage() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState(""); // status filter

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status_filter = filter;
      const { data } = await businessApi().get("/business/shipments/failed-and-returns", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente cases");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  React.useEffect(() => { load(); }, [load]);

  const requestRedelivery = async (id) => {
    try {
      await businessApi().post(`/business/shipments/${id}/redelivery-request`);
      toast.success("Genlevering anmodet — admin godkender inden 1 hverdag");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anmodning fejlede");
    }
  };

  const requestReturn = async (id) => {
    try {
      await businessApi().post(`/business/shipments/${id}/return-request`);
      toast.success("Return-to-sender anmodet — admin gennemgår");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anmodning fejlede");
    }
  };

  return (
    <div className="space-y-6" data-testid="business-failed-returns-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-brand-red font-bold">SPRINT 2G</div>
        <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">Mislykkede leveringer & returer</h1>
        <p className="text-brand-text2 mt-2">Få overblik over forsendelser der ikke kunne leveres, anmod om genlevering eller return-to-sender.</p>
      </header>

      {/* Filter */}
      <div className="flex flex-wrap gap-3 items-center bg-white border border-brand p-4">
        <label className="text-sm font-semibold text-brand-dark">Filter:</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-flat py-1.5" data-testid="biz-fr-filter">
          <option value="">{`Alle (${items.length})`}</option>
          <option value="failed_delivery">Mislykket levering</option>
          <option value="redelivery_requested">Genlevering anmodet</option>
          <option value="manual_review">Under behandling</option>
          <option value="returned">Returneret</option>
        </select>
        <button onClick={load} className="text-sm text-brand-text2 hover:text-brand-dark inline-flex items-center gap-1" data-testid="biz-fr-reload">
          <RefreshCw size={14}/> Opdater
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-brand-text2 inline-flex items-center gap-2"><Loader2 className="animate-spin"/> Indlæser...</div>
      ) : items.length === 0 ? (
        <div className="border border-brand bg-white p-12 text-center">
          <PackageX size={48} className="mx-auto text-brand-text2 mb-3"/>
          <h3 className="font-display font-bold text-xl text-brand-dark mb-2">Ingen mislykkede leveringer</h3>
          <p className="text-sm text-brand-text2">Alle dine forsendelser kører som forventet.</p>
        </div>
      ) : (
        <div className="bg-white border border-brand overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-3 py-2 text-left">Shipment #</th>
                <th className="px-3 py-2 text-left">Kunde-ref</th>
                <th className="px-3 py-2 text-left">Modtager / postnr</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Fejl-årsag</th>
                <th className="px-3 py-2 text-left">Tidspunkt</th>
                <th className="px-3 py-2 text-left">Foto</th>
                <th className="px-3 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const badge = STATUS_BADGES[s.status] || { label: s.status, cls: "bg-gray-100" };
                const reason = s.failed_delivery_reason_code ? (REASON_LABELS[s.failed_delivery_reason_code] || s.failed_delivery_reason_code) : "—";
                const ts = s.failed_delivery_at_iso || s.updated_at_iso;
                return (
                  <tr key={s.id} className="border-t border-brand" data-testid={`biz-fr-row-${s.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{s.shipment_number}</td>
                    <td className="px-3 py-2">{s.customer_reference || "—"}</td>
                    <td className="px-3 py-2">
                      <div>{s.recipient_name}</div>
                      <div className="text-xs text-brand-text2">{s.delivery_postal_code} {s.delivery_city}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">{reason}</td>
                    <td className="px-3 py-2 text-xs">{ts ? new Date(ts).toLocaleString("da-DK") : "—"}</td>
                    <td className="px-3 py-2">
                      {s.failed_delivery_photo_url ? (
                        <a href={s.failed_delivery_photo_url} target="_blank" rel="noreferrer" className="text-brand-red hover:text-brand-dark inline-flex items-center gap-1 text-xs">
                          <ImageIcon size={12}/> Se
                        </a>
                      ) : <span className="text-brand-text2 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-2 flex-wrap">
                        {s.status === "failed_delivery" && (
                          <>
                            <button onClick={() => requestRedelivery(s.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-white bg-brand-red hover:opacity-90" data-testid={`biz-fr-redeliver-${s.id}`}>
                              <RotateCcw size={11}/> Genlevering
                            </button>
                            <button onClick={() => requestReturn(s.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-brand-dark bg-brand-bg2 border border-brand hover:bg-white" data-testid={`biz-fr-return-${s.id}`}>
                              <ArrowLeftRight size={11}/> Returnér
                            </button>
                          </>
                        )}
                        <Link to={`/business/shipments/${s.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-brand-dark hover:text-brand-red">
                          <ExternalLink size={11}/> Vis
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
